import { type AnyType } from '@lightdash/common';
import {
    Button,
    Checkbox,
    Image,
    Loader,
    Paper,
    Stack,
    Text,
    Textarea,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconIdOff } from '@tabler/icons-react';
import html2canvas from 'html2canvas-pro';
import { useCallback, useEffect, useState, type FC } from 'react';
import { lightdashApi, networkHistory } from '../../api';
import MantineIcon from '../../components/common/MantineIcon';
import useToaster from '../../hooks/toaster/useToaster';
import classes from './SupportDrawerContent.module.css';

type SupportDrawerContentProps = {
    // Add props here
};

const MAX_LOG_LENGTH = 10;
const MAX_CAPTURE_VALUE_LENGTH = 500;
let logHistory: AnyType[] = [];

const toJsonSafeValue = (value: unknown): AnyType => {
    const seen = new WeakSet<object>();
    try {
        return JSON.parse(
            JSON.stringify(value, (_key, entry: unknown) => {
                if (typeof entry === 'bigint') return entry.toString();
                if (typeof entry === 'function' || typeof entry === 'symbol') {
                    return String(entry);
                }
                if (entry instanceof Error) {
                    return {
                        name: entry.name,
                        message: entry.message.substring(
                            0,
                            MAX_CAPTURE_VALUE_LENGTH,
                        ),
                    };
                }
                if (entry && typeof entry === 'object') {
                    if (seen.has(entry)) return '[Circular]';
                    seen.add(entry);
                }
                if (
                    typeof entry === 'string' &&
                    entry.length > MAX_CAPTURE_VALUE_LENGTH
                ) {
                    return entry.substring(0, MAX_CAPTURE_VALUE_LENGTH);
                }
                return entry;
            }),
        );
    } catch {
        return '[Unserializable value]';
    }
};

const getSafeNetworkHistory = () =>
    networkHistory.map(({ method, url, status }) => ({ method, url, status }));

/** This method will capture all the logs, and store it on memory
 * they will be shared when the user clicks on the share button
 * We only store the last 10 logs
 */
(function () {
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalInfo = console.info;

    function storeAndLog(type: AnyType, args: AnyType) {
        const message = {
            type,
            args: toJsonSafeValue(args),
            timestamp: new Date().toISOString(),
        };
        logHistory.push(message);
        if (logHistory.length > MAX_LOG_LENGTH) logHistory.shift();
    }

    console.error = function (...args) {
        storeAndLog('error', args);
        originalError.apply(console, args);
    };

    console.warn = function (...args) {
        storeAndLog('warn', args);
        originalWarn.apply(console, args);
    };

    console.info = function (...args) {
        storeAndLog('info', args);
        originalInfo.apply(console, args);
    };
})();

const SupportDrawerContent: FC<SupportDrawerContentProps> = () => {
    const [includeImage, setIncludeImage] = useState(true);
    const [moreDetails, setMoreDetails] = useState('');
    const [allowAccess, setAllowAccess] = useState(true);

    const [screenshot, setScreenshot] = useState<string | null>(null);
    const [screenshotError, setScreenshotError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showToastError, showToastSuccess } = useToaster();
    useEffect(() => {
        const element = document.querySelector('body');
        if (element) {
            void html2canvas(element as HTMLElement)
                .then((canvas) => {
                    const base64 = canvas.toDataURL('image/png');
                    setScreenshot(base64);
                })
                .catch(() => {
                    console.error('Failed to capture support screenshot');
                    setScreenshotError(true);
                    setScreenshot(null);
                });
        }
    }, []);

    const handleShare = useCallback(async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            await lightdashApi<null>({
                url: `/support/share`,
                method: 'POST',
                body: JSON.stringify({
                    image: includeImage ? screenshot : undefined,
                    logs: logHistory,
                    network: getSafeNetworkHistory(),
                    canImpersonate: allowAccess,
                    description: moreDetails.substring(0, 5000),
                }),
                sensitive: true,
            });
            showToastSuccess({
                title: 'Success',
                subtitle: 'Support request sent successfully',
            });
            modals.closeAll();
        } catch {
            showToastError({
                title: 'Failed to send support request',
                subtitle: 'Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    }, [
        allowAccess,
        includeImage,
        isSubmitting,
        moreDetails,
        screenshot,
        showToastError,
        showToastSuccess,
    ]);

    return (
        <Stack gap="xs">
            <Checkbox
                label="Include this image"
                checked={screenshotError ? false : includeImage}
                onChange={(event) => setIncludeImage(event.target.checked)}
                mt="xs"
                disabled={screenshotError}
            />

            {screenshot ? (
                <Image
                    height={200}
                    src={screenshot}
                    alt="Screenshot"
                    fit="contain"
                />
            ) : (
                <Paper p="lg" h={200}>
                    <Stack h="100%" align="center" justify="center" gap="xs">
                        {screenshotError ? (
                            <>
                                <MantineIcon icon={IconIdOff} color="ldGray" />
                                <Text c="dimmed" ta="center">
                                    Screenshot could not be captured. You can
                                    still submit your report with the details
                                    below
                                </Text>
                            </>
                        ) : (
                            <Loader height={200} w="100%" type="dots" />
                        )}
                    </Stack>
                </Paper>
            )}
            <Text mt="sm">
                Do you have and other details you'd like to share?
            </Text>
            <Textarea
                placeholder="Enter more details"
                value={moreDetails}
                onChange={(event) => setMoreDetails(event.target.value)}
                minRows={4}
            />
            <Checkbox
                label="Allow Lightdash support access to your instance"
                checked={allowAccess}
                onChange={(event) => setAllowAccess(event.target.checked)}
                mt="xs"
            />
            <Text size="xs" c="dimmed">
                By ticking this box, you agree to give Lightdash Support access
                to your organization for 12 hours to investigate this issue.
            </Text>

            <Text size="xs" c="dimmed">
                We will also share your Lightbox logs and your recent network
                requests to help us investigate this issue.
            </Text>
            <Button
                mt="xs"
                className={classes.submitButton}
                onClick={handleShare}
                loading={isSubmitting}
                disabled={isSubmitting}
            >
                Share
            </Button>
        </Stack>
    );
};

export default SupportDrawerContent;
